import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
  Chip,
  Avatar,
  Button,
  Dialog,
  alpha,
} from '@mui/material';
import {
  Close,
  People,
  Settings,
  BarChart,
  Security,
  History,
  BugReport,
  Storage,
  Menu as MenuIcon,
  Business,
  Language,
  Dashboard,
  Layers
} from '@mui/icons-material';
import AdminUsers from './AdminUsers';
import AdminUsersRedesigned from './AdminUsersRedesigned';
import { OrganizationsManager } from './OrganizationsManager';
import { ClientsManager } from './ClientsManager';
import { DatabasesManager } from './DatabasesManager';
import { Dashboard1 } from './Dashboard';
import { SemanticLayerManager } from './SemanticLayerManager';
import { useUser } from './UserContext';

const DARK_COLORS = {
  PRIMARY: "#000000",
  SECONDARY: "#1a1a1a",
  ACCENT: "#2d2d2d",
  SURFACE: "#121212",
  TEXT_PRIMARY: "#ffffff",
  TEXT_SECONDARY: "#b0b0b0",
  BORDER: "#333333",
  HOVER: "#404040",
  SUCCESS: "#4caf50",
  WARNING: "#ff9800",
  INFO: "#2196f3",
  ADMIN: "#9c27b0",
  ERROR: "#ff4444",
};

interface AdminPanelProps {
  open: boolean;
  onClose: () => void;
  userRole?: 'user' | 'admin' | 'superadmin' | 'super_admin';
  userName?: string;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ open, onClose, userRole = 'user', userName = 'Admin' }) => {
  const [selectedSection, setSelectedSection] = useState('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [dashboardDialogOpen, setDashboardDialogOpen] = useState(false);
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [organizationsDialogOpen, setOrganizationsDialogOpen] = useState(false);
  const [clientsDialogOpen, setClientsDialogOpen] = useState(false);
  const [databasesDialogOpen, setDatabasesDialogOpen] = useState(false);
  const [hierarchicalDashboardOpen, setHierarchicalDashboardOpen] = useState(false);
  const [semanticLayerDialogOpen, setSemanticLayerDialogOpen] = useState(false);
  const { user } = useUser();

  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'superadmin';

  // User list and management moved to `AdminUsers` component.

  const menuItems = [
    ...(isSuperAdmin ? [
      { id: 'hierarchical', label: 'Dashboard', icon: <Dashboard />, badge: null },
      { id: 'organizations', label: 'Organizzazioni', icon: <Language />, badge: null },
    ] : []),
    { id: 'clients', label: 'Clienti', icon: <Business />, badge: null },
    { id: 'users', label: 'Gestione Utenti', icon: <People />, badge: null },
    { id: 'databases', label: 'Database', icon: <Storage />, badge: null },
    { id: 'semantic-layer', label: 'Semantic Layer', icon: <Layers />, badge: null },
    { id: 'analytics', label: 'Analytics', icon: <BarChart />, badge: null },
    { id: 'logs', label: 'Log Sistema', icon: <History />, badge: null },
    { id: 'security', label: 'Sicurezza', icon: <Security />, badge: null },
  ];

  return (
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 500, md: 650 },
            backgroundColor: DARK_COLORS.PRIMARY,
            backgroundImage: `
              radial-gradient(circle at 20% 10%, rgba(33, 150, 243, 0.08) 0%, transparent 40%),
              radial-gradient(circle at 80% 90%, rgba(76, 175, 80, 0.06) 0%, transparent 40%),
              linear-gradient(180deg, ${DARK_COLORS.PRIMARY} 0%, ${DARK_COLORS.SURFACE} 100%)
            `,
            borderLeft: `1px solid ${DARK_COLORS.ACCENT}`,
          }
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: `1px solid ${DARK_COLORS.ACCENT}`,
            background: `linear-gradient(135deg, ${DARK_COLORS.SECONDARY} 0%, ${DARK_COLORS.PRIMARY} 100%)`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: `linear-gradient(90deg, ${DARK_COLORS.INFO}, ${DARK_COLORS.SUCCESS})`,
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  bgcolor: alpha(DARK_COLORS.INFO, 0.2),
                  width: 48,
                  height: 48,
                  border: `2px solid ${DARK_COLORS.INFO}`,
                  boxShadow: `0 0 20px ${alpha(DARK_COLORS.INFO, 0.4)}`,
                }}
              >
                <Security sx={{ color: DARK_COLORS.INFO }} />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ 
                  color: DARK_COLORS.TEXT_PRIMARY, 
                  fontWeight: 700,
                  letterSpacing: '0.5px'
                }}>
                  Admin Panel
                </Typography>
                <Chip
                  label={userRole?.toUpperCase() || "ADMIN"}
                  size="small"
                  sx={{
                    mt: 0.5,
                    bgcolor: alpha(DARK_COLORS.SUCCESS, 0.2),
                    color: DARK_COLORS.SUCCESS,
                    border: `1px solid ${DARK_COLORS.SUCCESS}`,
                    fontSize: '0.7rem',
                    height: 20,
                    fontWeight: 700,
                  }}
                />
              </Box>
            </Box>
            <IconButton
              onClick={onClose}
              sx={{
                color: DARK_COLORS.TEXT_SECONDARY,
                bgcolor: DARK_COLORS.ACCENT,
                width: 36,
                height: 36,
                '&:hover': {
                  color: DARK_COLORS.TEXT_PRIMARY,
                  backgroundColor: DARK_COLORS.HOVER,
                  transform: 'rotate(90deg)',
                  boxShadow: `0 0 12px ${alpha(DARK_COLORS.ERROR, 0.3)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <Close />
            </IconButton>
          </Box>
          
          <Typography variant="caption" sx={{ 
            color: DARK_COLORS.TEXT_SECONDARY,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            {userName} • <Box component="span" sx={{ 
              width: 6, 
              height: 6, 
              borderRadius: '50%', 
              bgcolor: DARK_COLORS.SUCCESS,
              display: 'inline-block',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.5 }
              }
            }} /> Online
          </Typography>
        </Box>

        {/* Menu Items */}
        <List sx={{ p: 2 }}>
          {menuItems.map((item, index) => (
            <React.Fragment key={item.id}>
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                    selected={selectedSection === item.id}
                    onClick={() => {
                      if (item.id === 'hierarchical') {
                        setHierarchicalDashboardOpen(true);
                      } else if (item.id === 'dashboard') {
                        setDashboardDialogOpen(true);
                      } else if (item.id === 'users') {
                        setUsersDialogOpen(true);
                      } else if (item.id === 'organizations') {
                        setOrganizationsDialogOpen(true);
                      } else if (item.id === 'clients') {
                        setClientsDialogOpen(true);
                      } else if (item.id === 'databases') {
                        setDatabasesDialogOpen(true);
                      } else if (item.id === 'semantic-layer') {
                        setSemanticLayerDialogOpen(true);
                      } else {
                        setSelectedSection(item.id);
                      }
                    }}
                  sx={{
                    borderRadius: '12px',
                    border: `1px solid transparent`,
                    '&.Mui-selected': {
                      backgroundColor: alpha(DARK_COLORS.INFO, 0.15),
                      border: `1px solid ${alpha(DARK_COLORS.INFO, 0.3)}`,
                      boxShadow: `0 0 16px ${alpha(DARK_COLORS.INFO, 0.2)}`,
                      '&:hover': {
                        backgroundColor: alpha(DARK_COLORS.INFO, 0.2),
                      },
                    },
                    '&:hover': {
                      backgroundColor: DARK_COLORS.HOVER,
                      border: `1px solid ${DARK_COLORS.BORDER}`,
                      transform: 'translateX(4px)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: selectedSection === item.id ? DARK_COLORS.INFO : DARK_COLORS.TEXT_PRIMARY, 
                    minWidth: 40,
                    transition: 'color 0.2s'
                  }}>
                    {item.badge ? (
                      <Badge badgeContent={item.badge} color="error">
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      sx: { 
                        color: selectedSection === item.id ? DARK_COLORS.INFO : DARK_COLORS.TEXT_PRIMARY, 
                        fontSize: '0.95rem',
                        fontWeight: selectedSection === item.id ? 600 : 400,
                        transition: 'all 0.2s'
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
              {(index === 2 || index === 5) && (
                <Divider 
                  sx={{ 
                    my: 2, 
                    borderColor: DARK_COLORS.ACCENT,
                    opacity: 0.3
                  }} 
                />
              )}
            </React.Fragment>
          ))}
        </List>



        {/* Users dialog (full screen redesigned) */}
        <Dialog
          open={usersDialogOpen}
          onClose={() => setUsersDialogOpen(false)}
          fullScreen
          PaperProps={{ 
            sx: { 
              backgroundColor: DARK_COLORS.PRIMARY,
              m: 0,
              borderRadius: 0
            } 
          }}
        >
          <AdminUsersRedesigned onClose={() => setUsersDialogOpen(false)} />
        </Dialog>

        {/* Organizations dialog (только для super_admin) */}
        <Dialog
          open={organizationsDialogOpen}
          onClose={() => setOrganizationsDialogOpen(false)}
          fullScreen
          PaperProps={{ 
            sx: { 
              backgroundColor: DARK_COLORS.PRIMARY,
              m: 0,
              borderRadius: 0
            } 
          }}
        >
          <OrganizationsManager onClose={() => setOrganizationsDialogOpen(false)} />
        </Dialog>

        {/* Clients dialog */}
        <Dialog
          open={clientsDialogOpen}
          onClose={() => setClientsDialogOpen(false)}
          fullScreen
          PaperProps={{ 
            sx: { 
              backgroundColor: DARK_COLORS.PRIMARY,
              m: 0,
              borderRadius: 0
            } 
          }}
        >
          <ClientsManager onClose={() => setClientsDialogOpen(false)} />
        </Dialog>

        {/* Databases dialog */}
        <Dialog
          open={databasesDialogOpen}
          onClose={() => setDatabasesDialogOpen(false)}
          fullScreen
          PaperProps={{ 
            sx: { 
              backgroundColor: DARK_COLORS.PRIMARY,
              m: 0,
              borderRadius: 0
            } 
          }}
        >
          <DatabasesManager onClose={() => setDatabasesDialogOpen(false)} />
        </Dialog>

        {/* Semantic Layer Dialog */}
        <Dialog
          open={semanticLayerDialogOpen}
          onClose={() => setSemanticLayerDialogOpen(false)}
          fullScreen
          PaperProps={{ 
            sx: { 
              backgroundColor: DARK_COLORS.PRIMARY,
              m: 0,
              borderRadius: 0
            } 
          }}
        >
          <SemanticLayerManager
            onClose={() => setSemanticLayerDialogOpen(false)}
            tenant="camngo"
          />
        </Dialog>

        {/* Hierarchical Dashboard Dialog */}
        <Dialog
          open={hierarchicalDashboardOpen}
          onClose={() => setHierarchicalDashboardOpen(false)}
          fullScreen
          PaperProps={{ 
            sx: { 
              backgroundColor: DARK_COLORS.PRIMARY,
              m: 0,
              borderRadius: 0
            } 
          }}
        >
          <Dashboard1 />
        </Dialog>

        {/* Footer */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            p: 2,
            borderTop: `1px solid ${DARK_COLORS.ACCENT}`,
            background: `linear-gradient(180deg, ${DARK_COLORS.PRIMARY} 0%, ${DARK_COLORS.SECONDARY} 100%)`,
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ 
              color: DARK_COLORS.TEXT_SECONDARY,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <Box component="span" sx={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                bgcolor: DARK_COLORS.SUCCESS,
                display: 'inline-block'
              }} />
              v1.0.0 • Sistema monitorato
            </Typography>
            <Typography variant="caption" sx={{ 
              color: alpha(DARK_COLORS.TEXT_SECONDARY, 0.5),
              fontSize: '0.65rem'
            }}>
              Admin Console
            </Typography>
          </Box>
        </Box>
      </Drawer>
  );
};

export default AdminPanel;